import { useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router'
import { useCallback } from 'react'

/** Typed view over the current route's validated search params. */
export function useSearchParams<T extends Record<string, unknown>>(): T {
  return useSearch({ strict: false }) as T
}

/** Merge a patch into the search params (replace history entry by default). */
export function useSetSearch<T extends Record<string, unknown>>() {
  const navigate = useNavigate()
  return useCallback(
    (patch: Partial<T>, replace = true) => {
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...patch }
          for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k]
          return next
        }) as never,
        replace,
      })
    },
    [navigate],
  )
}

export function useParam(name: string): string {
  const p = useParams({ strict: false }) as Record<string, string | undefined>
  return p[name] ?? ''
}

/** Navigate to a full href (path + optional query string). */
export function useGo() {
  const router = useRouter()
  return useCallback((href: string) => router.history.push(href), [router])
}
